import { styled, TextField, useTheme } from '@mui/material';
import { FC, useEffect, useRef, useState } from 'react';
import { Typography } from '../Typography/Typography';
import { Modal } from '../Modal/Modal';
import { EditSurveyArgs, EVALUATION_SCALE, EvaluationRule, Survey, SURVEY_TYPE } from '../../types';
import { Switch } from '../Switch';
import { EvaluationRulesConfig } from './EvaluationRulesConfig/EvaluationRulesConfig';
import { getEvaluationRules } from '../../utils/getEvaluationRules';

interface Props {
  title: string;
  surveyName: string;
  onConfirm: (args: EditSurveyArgs) => void;
  confirmText?: string;
  onClose?: () => void;
  isOpen: boolean;
  activeSurvey: Survey | null;
}

const Wrapper = styled('div')``;

const SwitchesContainer = styled('div')`
  margin-top: 16px;
`;

const evaluationRulesBackwardsCompatibility = (rules: EvaluationRule[]) => {
  if (!rules?.every?.((rule) => Object.hasOwn(rule, 'color'))) {
    return getEvaluationRules(EVALUATION_SCALE.BINARY);
  }

  return rules;
};

export const EditSurveyModal: FC<Props> = ({
  title,
  onConfirm,
  surveyName,
  confirmText = 'Confirm',
  onClose = () => undefined,
  isOpen,
  activeSurvey,
}) => {
  const { colors } = useTheme();
  const [inputValue, setInputValue] = useState(surveyName);
  const [description, setDescription] = useState(
    activeSurvey?.description ? activeSurvey.description : ''
  );
  const inputRef = useRef<HTMLInputElement>(null);

  const [includeEvaluation, setIncludeEvaluation] = useState(
    activeSurvey?.includeEvaluation || false
  );
  const [includeWeight, setIncludeWeight] = useState(activeSurvey?.includeWeight || false);
  const [includeQuestionNumbers, setIncludeQuestionNumbers] = useState(
    activeSurvey?.includeQuestionNumbers || false
  );
  const [evaluationRules, setEvaluationRules] = useState(
    activeSurvey?.evaluationRules?.length
      ? evaluationRulesBackwardsCompatibility(activeSurvey?.evaluationRules)
      : getEvaluationRules(activeSurvey?.evaluationScale || EVALUATION_SCALE.BINARY)
  );
  const [evaluationScale, setEvaluationScale] = useState(
    activeSurvey?.evaluationScale || EVALUATION_SCALE.BINARY
  );

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
  };

  const onDescriptionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setDescription(e.target.value);
  };

  return (
    <Modal
      onClose={onClose}
      isOpen={isOpen}
      title={title}
      onConfirm={() =>
        onConfirm({
          newName: inputValue,
          description,
          includeEvaluation,
          includeWeight,
          includeQuestionNumbers,
          evaluationRules,
          evaluationScale,
        })
      }
      cancelBtnText='Cancel'
      confirmBtnText={confirmText}
    >
      <Wrapper>
        <Typography variant='caption' color={colors.primary[90]} style={{ paddingBottom: '10px' }}>
          Name
        </Typography>
        <TextField
          value={inputValue}
          onChange={onChange}
          placeholder='Survey name'
          style={{ width: '100%' }}
          inputRef={inputRef}
        />
        <Typography
          variant='caption'
          color={colors.primary[90]}
          style={{ paddingBottom: '10px', marginTop: '16px' }}
        >
          Description
        </Typography>
        <TextField
          value={description}
          onChange={onDescriptionChange}
          placeholder='Description'
          style={{ width: '100%' }}
        />
        <SwitchesContainer>
          <Switch
            checked={!!includeQuestionNumbers}
            onChange={() => setIncludeQuestionNumbers((prev) => !prev)}
            label='Show Question Numbers'
          />
          {!activeSurvey ||
          (activeSurvey?.type === SURVEY_TYPE.USER && !activeSurvey?.isAssigned) ? (
            <>
              <EvaluationRulesConfig
                includeEvaluation={includeEvaluation}
                toggleIncludeEvaluation={() => setIncludeEvaluation((prev) => !prev)}
                evaluationRules={evaluationRules}
                evaluationScale={evaluationScale}
                setEvaluationRules={setEvaluationRules}
                setEvaluationScale={setEvaluationScale}
              />
              <Switch
                checked={!!includeWeight}
                onChange={() => setIncludeWeight((prev) => !prev)}
                label='Include question weights'
              />
            </>
          ) : null}
        </SwitchesContainer>
      </Wrapper>
    </Modal>
  );
};
